import React, { useState } from 'react';
import { useLogin, useNotify } from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    CircularProgress,
    useTheme,
    useMediaQuery,
    InputAdornment,
    IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, Lock, Person } from '@mui/icons-material';

const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const login = useLogin();
    const notify = useNotify();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        login({ username: email, password }).catch(() => {
            notify('Invalid email or password', { type: 'warning' });
            setLoading(false);
        });
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #7A68A7 0%, #9B8BC4 100%)',
                padding: isMobile ? 2 : 0,
            }}
        >
            <Card
                sx={{
                    minWidth: 300,
                    maxWidth: 400,
                    width: '100%',
                    borderRadius: isMobile ? 2 : 4,
                    boxShadow: isMobile ? 'none' : '0 8px 32px rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        p: 3,
                        textAlign: 'center',
                        bgcolor: 'primary.main',
                        color: 'white',
                    }}
                >
                    <Typography variant="h5" component="h1" fontWeight="bold">
                        CatchU Admin
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                        관리자 로그인
                    </Typography>
                </Box>

                <CardContent sx={{ p: 4 }}>
                    <form onSubmit={handleSubmit}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <TextField
                                label="이메일"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                fullWidth
                                required
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Person color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    },
                                }}
                            />

                            <TextField
                                label="비밀번호"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                fullWidth
                                required
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lock color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    },
                                }}
                            />

                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                fullWidth
                                size="large"
                                disabled={loading}
                                sx={{
                                    mt: 1,
                                    py: 1.5,
                                    borderRadius: 2,
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    boxShadow: '0 4px 12px rgba(122, 104, 167, 0.3)',
                                    '&:hover': {
                                        boxShadow: '0 6px 16px rgba(122, 104, 167, 0.4)',
                                    },
                                }}
                            >
                                {loading ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    '로그인'
                                )}
                            </Button>
                        </Box>
                    </form>
                </CardContent>
            </Card>

            <Typography
                variant="caption"
                sx={{
                    mt: 4,
                    color: 'rgba(255, 255, 255, 0.8)',
                    textAlign: 'center',
                }}
            >
                © 2025 CatchU. All rights reserved.
            </Typography>
        </Box>
    );
};

export default LoginPage;
